import {
    Grid,
    Typography,
    TextField,
    Button,
    Paper,
    CircularProgress,
    Snackbar,
    Alert,
    Box,
    useMediaQuery,
    Divider,
    Autocomplete,
    InputLabel
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NavigatedComponent from "../NavigatedComponent";
import Header from "../../common/Header";
import Sidenav from "../../common/Sidenav";
import IconSidenav from "../../common/IconSidenav";
import { apiList, invokeApi, invokeFormDataApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";
import { DropzoneArea } from "mui-file-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const AddPdf = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [cookies] = useCookies();
    const location = useLocation();

    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("");
    const [subcategory, setSubcategory] = useState("");
    const [pdfUrl, setPdfUrl] = useState("");
    const [imageUrl, setImageUrl] = useState("");

    const [pdfFile, setPdfFile] = useState(null);
    const [imageFile, setImageFile] = useState(null);

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [showSideNav, setShowSideNav] = useState(false);
    const [existingCategories, setExistingCategories] = useState([]);
    const [existingSubcategories, setExistingSubcategories] = useState([]);

    const isMobileScreen = useMediaQuery("(min-width:360px) and (max-width:500px)");

    useEffect(() => {
        if (id) {
            fetchPdfData();
        }
        fetchCategoriesAndSubcategories();
    }, [id]);

    const toggleSideNav = () => {
        setShowSideNav(!showSideNav);
    };

    const fetchCategoriesAndSubcategories = async () => {
        try {
            const response = await invokeApi(config.getMyCollege + apiList.getPdfPublications, {}, cookies);
            if (response?.status === 200 && response?.data?.pdfs) {
                const pdfs = response.data.pdfs;
                const categories = [...new Set(pdfs.map(pdf => pdf.categoryName || pdf.category || pdf.category_name).filter(Boolean))];
                setExistingCategories(categories);

                const subcategories = [...new Set(pdfs.map(pdf => pdf.subcategoryName || pdf.subcategory || pdf.subcategory_name).filter(Boolean))];
                setExistingSubcategories(subcategories);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const fetchPdfData = async () => {
        setFetching(true);
        try {
            const response = await invokeApi(config.getMyCollege + apiList.getPdfPublication, { id }, cookies);
            if (response?.status === 200 && response?.data?.pdf) {
                const { title, categoryName, category, subcategory, subcategoryName, pdfUrl, imageUrl } = response.data.pdf;
                setTitle(title);
                setCategory(categoryName || category || "");
                setSubcategory(subcategoryName || subcategory || "");
                setPdfUrl(pdfUrl || "");
                setImageUrl(imageUrl || "");
            } else {
                setSnackbar({ open: true, message: "Failed to load details", severity: "error" });
            }
        } catch (error) {
            console.error("Error fetching PDF details:", error);
        } finally {
            setFetching(false);
        }
    };

    const handleImageUpload = async () => {
        if (!imageFile) return imageUrl;
        const formData = new FormData();
        formData.append("featured", imageFile);

        try {
            const response = await invokeFormDataApi(config.getMyCollege + apiList.addPublicationFeaturedImage, formData, cookies, true);
            if (response?.status === 200) {
                return response.data.imageUrl || "";
            }
            return null;
        } catch (err) {
            console.error("Image upload failed", err);
            return null;
        }
    };

    const handlePdfUpload = async () => {
        if (!pdfFile) return pdfUrl;
        const formData = new FormData();
        formData.append("pdf", pdfFile);

        try {
            const response = await invokeFormDataApi(config.getMyCollege + apiList.uploadPublicationPdf, formData, cookies, true);
            if (response?.status === 200) {
                return response.data.pdfUrl || "";
            }
            return null;
        } catch (err) {
            console.error("PDF upload failed", err);
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title || !category || !subcategory || (!pdfUrl && !pdfFile) || (!imageUrl && !imageFile)) {
            setSnackbar({ open: true, message: "Please fill all required fields", severity: "warning" });
            return;
        }

        setLoading(true);

        const uploadedImageUrl = await handleImageUpload();
        if (imageFile && !uploadedImageUrl) {
            setSnackbar({ open: true, message: "Image upload failed", severity: "error" });
            setLoading(false);
            return;
        }

        const uploadedPdfUrl = await handlePdfUpload();
        if (pdfFile && !uploadedPdfUrl) {
            setSnackbar({ open: true, message: "PDF upload failed", severity: "error" });
            setLoading(false);
            return;
        }

        const api = id ? apiList.updatePdfPublication : apiList.addPdfPublication;
        const payload = {
            title,
            categoryName: category,
            subcategoryName: subcategory,
            pdfUrl: uploadedPdfUrl,
            imageUrl: uploadedImageUrl,
            type: 'pdf',
            ...(id && { id })
        };

        try {
            const response = await invokeApi(config.getMyCollege + api, payload, cookies);
            if (response?.status >= 200 && response?.status < 300) {
                setSnackbar({ open: true, message: `Publication ${id ? "updated" : "added"} successfully`, severity: "success" });
                setTimeout(() => navigate("/publication-manager/pdf-list"), 1500);
            } else {
                setSnackbar({ open: true, message: response.data?.message || "Operation failed", severity: "error" });
            }
        } catch (error) {
            console.error("Error saving publication:", error);
            setSnackbar({ open: true, message: "Something went wrong", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Grid sx={{ display: "flex" }}>
            {isMobileScreen ? (
                <IconSidenav />
            ) : showSideNav ? (
                <IconSidenav />
            ) : (
                <Sidenav />
            )}
            <Grid component="main" sx={{ width: "100%", px: 1 }}>
                <Header toggleSideNav={toggleSideNav} />
                <Grid sx={{ py: 1 }}>
                    <NavigatedComponent pathname={location.pathname} />
                    <Grid container justifyContent="center" sx={{ p: 3 }}>
                        <Grid item xs={12} md={10}>
                            <Paper sx={{ p: 4, my: 2 }}>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    {id ? "Edit Report/PDF" : "Add Report/PDF"}
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <form onSubmit={handleSubmit}>
                                    <TextField
                                        fullWidth
                                        label="Title"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        margin="normal"
                                        required
                                    />
                                    <Autocomplete
                                        freeSolo
                                        options={existingCategories}
                                        value={category}
                                        onChange={(event, newValue) => {
                                            setCategory(newValue);
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            setCategory(newInputValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Category"
                                                margin="normal"
                                                required
                                                fullWidth
                                                placeholder="e.g., Education Outreach, Reports, Policy Briefs"
                                            />
                                        )}
                                    />
                                    <Autocomplete
                                        freeSolo
                                        options={existingSubcategories}
                                        value={subcategory}
                                        onChange={(event, newValue) => {
                                            setSubcategory(newValue);
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            setSubcategory(newInputValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Subcategory"
                                                margin="normal"
                                                required
                                                fullWidth
                                                placeholder="e.g., 2024, 2025"
                                            />
                                        )}
                                    />

                                    <Grid item xs={12} sx={{ my: 2 }}>
                                        <InputLabel sx={{ fontWeight: "bold", my: 1, fontSize: 15 }}>
                                            Upload PDF * {pdfUrl && "(Current: " + pdfUrl.split('/').pop() + ")"}
                                        </InputLabel>
                                        <DropzoneArea
                                            filesLimit={1}
                                            onChange={(files) => setPdfFile(files[0] || null)}
                                            acceptedFiles={[".pdf", "application/pdf"]}
                                            maxFileSize={50000000}
                                            showPreviews={true}
                                            showPreviewsInDropzone={false}
                                            Icon={() => (
                                                <CloudUploadIcon sx={{ color: "#1d1a57", width: 35, height: 35 }} />
                                            )}
                                            dropzoneText={
                                                <Typography sx={{ color: "#1d1a57", fontWeight: 600 }}>
                                                    Upload / Drag PDF here
                                                </Typography>
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} sx={{ my: 2 }}>
                                        <InputLabel sx={{ fontWeight: "bold", my: 1, fontSize: 15 }}>
                                            Cover Image * {imageUrl && "(Current image exists)"}
                                        </InputLabel>
                                        <DropzoneArea
                                            filesLimit={1}
                                            onChange={(files) => setImageFile(files[0] || null)}
                                            acceptedFiles={["image/jpeg", "image/png", "image/jpg"]}
                                            maxFileSize={5000000}
                                            showPreviews={true}
                                            showPreviewsInDropzone={false}
                                            Icon={() => (
                                                <CloudUploadIcon sx={{ color: "#1d1a57", width: 35, height: 35 }} />
                                            )}
                                            dropzoneText={
                                                <Typography sx={{ color: "#1d1a57", fontWeight: 600 }}>
                                                    Upload / Drag cover image here
                                                </Typography>
                                            }
                                        />
                                        {imageUrl && !imageFile && (
                                            <Box sx={{ mt: 2 }}>
                                                <img src={imageUrl} alt="Cover" style={{ width: 100, height: 140, objectFit: 'cover' }} />
                                            </Box>
                                        )}
                                    </Grid>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        sx={{ mt: 3, bgcolor: "#262272" }}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : id ? "Update Publication" : "Add Publication"}
                                    </Button>
                                </form>
                            </Paper>
                        </Grid>
                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={6000}
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                        >
                            <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                                {snackbar.message}
                            </Alert>
                        </Snackbar>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AddPdf;

